import sal from "sal.js";
import { prefersReducedMotion } from "../utils/reduced-motion";

let salInstance = null;
let salStartRaf = 0;
let salStartTimer = 0;

const salOptions = () => ({
  // Fire as soon as any part enters the (expanded) viewport.
  threshold: 0.01,
  // Positive bottom margin = start the reveal slightly before the element
  // reaches the fold (negative margin was delaying it too far in).
  rootMargin: "0px 0px 20% 0px",
  once: true,
  // Must be a boolean: sal treats any truthy `disabled` (incl. a function) as on.
  disabled: prefersReducedMotion(),
});

function cancelPendingStart() {
  if (salStartTimer) {
    clearTimeout(salStartTimer);
    salStartTimer = 0;
  }

  if (salStartRaf) {
    cancelAnimationFrame(salStartRaf);
    salStartRaf = 0;
  }
}

function disposeSal() {
  cancelPendingStart();

  if (!salInstance) {
    return;
  }

  try {
    salInstance.disable();
  } catch (error) {
    // Swup may have removed observed nodes; ignore dispose errors.
  }

  salInstance = null;

  // disable() adds body.sal-disabled (forces opacity:1). Clear it unless
  // reduced motion should keep reveals fully visible.
  if (!prefersReducedMotion()) {
    document.body.classList.remove("sal-disabled");
  }
}

/** Tear down before Swup replaces #main so a stale observer cannot animate early. */
export function teardownSal() {
  disposeSal();
  document.querySelectorAll("[data-sal]").forEach((el) => {
    el.classList.remove("sal-animate");
  });
}

/**
 * Initialize (or re-initialize after Swup) scroll animations.
 * Uses existing [data-sal] markup; respects prefers-reduced-motion.
 *
 * Waits two animation frames after clearing `.sal-animate` so the hidden
 * state paints before IntersectionObserver adds the class — otherwise
 * above-the-fold elements "animate" with no visible transition.
 */
export function initSal() {
  const options = salOptions();

  disposeSal();

  document.querySelectorAll("[data-sal]").forEach((el) => {
    el.classList.remove("sal-animate");
  });

  if (options.disabled) {
    document.body.classList.add("sal-disabled");
    return null;
  }

  // Brief delay + two frames so the page (and Swup enter fade) can settle and
  // opacity:0 paints before IntersectionObserver adds .sal-animate.
  void document.body.offsetHeight;

  salStartTimer = window.setTimeout(() => {
    salStartTimer = 0;
    void document.body.offsetHeight;
    salStartRaf = requestAnimationFrame(() => {
      salStartRaf = requestAnimationFrame(() => {
        salStartRaf = 0;
        salInstance = sal(options);
      });
    });
  }, 80);

  return salInstance;
}

/**
 * Observe newly inserted / newly visible [data-sal] elements.
 * Does not create an instance — wait for initSal() so reveals are not
 * triggered while the page (or a view) is still invisible.
 */
export function updateSal() {
  if (!salInstance) {
    return null;
  }

  if (prefersReducedMotion()) {
    try {
      salInstance.disable();
    } catch (error) {
      disposeSal();
      document.body.classList.add("sal-disabled");
      return null;
    }
    return salInstance;
  }

  try {
    salInstance.update();
  } catch (error) {
    return initSal();
  }

  return salInstance;
}
